import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { usePartyMutation } from "Api/Mutations/Party/usePartyMutation";
import { getPartyQueryKey } from "Api/Queries/Party/usePartyQuery";
import { useCreatePartyWithRelation } from "Hooks/useCreatePartyWithRelation";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { FormSection } from "Components/Shared/FormSection";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { PhoneInput } from "Components/Shared/Inputs/Form/PhoneInput";
import { getContactPersonSchema } from "Components/Shared/Merchant/ContactPersons/schema";
import {
  type CountryCode,
  Gender,
  PartyType,
  RelationType,
  VatStatus,
} from "Infrastructure/Api/Api";
import { NO_VAT_NUMBER } from "Schemas/vatNumberSchema";
import { EMPTY_PHONE } from "Utils/PhoneUtils";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import type { z } from "zod";

const COMPONENT_RESOURCES = Resources.Components.Merchant.ContactPersons.Form;

type ContactPersonForm = z.infer<ReturnType<typeof getContactPersonSchema>>;

type Props = {
  owningPartyID: string;
  contactPerson: Partial<ContactPersonForm>;
  /** Further citizenships persisted on the edited party — the form has no nationality field, so the
   *  update must round-trip the current value instead of wiping it with an empty list. */
  currentOtherNationalities?: Array<CountryCode>;
  onAfterSubmit?: (isSuccess: boolean) => void;
};

export const ContactPersonForm = ({
  contactPerson,
  currentOtherNationalities,
  owningPartyID,
  onAfterSubmit,
}: Props) => {
  const queryClient = useQueryClient();
  const { t } = useResource();

  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = useForm<ContactPersonForm>({
    resolver: zodResolver(getContactPersonSchema(t)),
    defaultValues: {
      ...contactPerson,
      phone: contactPerson.phone ?? EMPTY_PHONE,
    },
  });

  const { postPartyWithRelation, isPending: isCreatePartyPending } =
    useCreatePartyWithRelation({
      owningPartyID,
      relationType: RelationType.ContactPerson,
      onSuccess: () => {
        toast.success(t(COMPONENT_RESOURCES.AddSuccess));
        onAfterSubmit?.(true);
        queryClient.invalidateQueries({
          queryKey: getPartyQueryKey(owningPartyID),
        });
      },
      onError: () => {
        toast.error(t(COMPONENT_RESOURCES.AddError));
        onAfterSubmit?.(false);
      },
    });

  const { mutate: updateParty, isPending: isUpdatePartyPending } =
    usePartyMutation({
      onSuccess: () => {
        toast.success(t(COMPONENT_RESOURCES.UpdateSuccess));
        onAfterSubmit?.(true);
        queryClient.invalidateQueries({
          queryKey: getPartyQueryKey(owningPartyID),
        });
      },
      onError: () => {
        toast.error(t(COMPONENT_RESOURCES.UpdateError));
        onAfterSubmit?.(false);
      },
    });

  const onSubmit = (data: ContactPersonForm) => {
    if (data.publicID) {
      updateParty({
        publicID: data.publicID,
        data: {
          ...data,
          gender: Gender.NotSet,
          type: PartyType.NaturalPerson,
          isForeigner: false,
          licenses: [],
          addresses: [],
          relatedParties: [],
          // Required by the DTO shape only — the server ignores them on updates.
          verifiedPhone: EMPTY_PHONE,
          vatStatus: VatStatus.Unknown,
          vatNumber: NO_VAT_NUMBER, // a natural person
          otherNationalities: currentOtherNationalities ?? [],
        },
      });
    } else {
      postPartyWithRelation({
        ...data,
        gender: Gender.NotSet,
        vatNumber: NO_VAT_NUMBER,
        otherNationalities: [],
      });
    }
  };

  const isPending = isCreatePartyPending || isUpdatePartyPending;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormSection legend={t(COMPONENT_RESOURCES.Legend)}>
        <Box>
          <FormInput
            control={control}
            name="firstName"
            label={t(Resources.Common.FirstName)}
          />
          <FormInput
            control={control}
            name="lastName"
            label={t(Resources.Common.LastName)}
          />
          <PhoneInput
            control={control}
            name="phone"
            label={t(Resources.Common.Phone)}
          />
          <FormInput
            control={control}
            name="email"
            label={t(Resources.Common.Email)}
          />
        </Box>
      </FormSection>
      <PrimaryButton
        fullWidth
        type="submit"
        isLoading={isPending}
        variant="contained"
        disabled={!isDirty}
        color="primary"
      >
        {t(Resources.Common.Save)}
      </PrimaryButton>
    </form>
  );
};
